import { css } from 'styled-components';

import { VALID_PLACEMENTS } from '../Header/Dropdown/Drawer/StyledHeaderDrawer';
import { themeMerge } from '../Utils';

import IThemeInterface, { breakPointSizes, edgeSize, zIndices } from './common';

const buttonFocusOverride: Partial<IThemeInterface['button']['base']> = {
  outline: { offset: '2.5px' },
};

const theme: IThemeInterface = themeMerge({
  name: 'amount',

  global: {
    breakPointSizes,

    colors: {
      blackberry: '#022a3a',
      blackberry120: '#01212e',
      gray800: '#264856',
      gray700: '#4a6672',
      gray600: '#677f89',
      gray500: '#919ea3',
      gray400: '#ccd4d8',
      gray350: '#ebedee',
      gray300: '#f2f2f2',
      gray200: '#f2f4f5',
      gray100: '#f8f8f8',
      gray50: '#fafafa',
      pomegranate: '#e91c29',
      pomegranate120: '#c8000f',
      pomegranate40: '#f6a4a9',
      pomegranate20: '#fbd2d4',
      electricAvocado: '#7befb2',
      electricAvocado20: '#e5fcf0',
      mango: '#ffd22a',
      mango20: '#fff6d4',

      accent: 'pomegranate',
      accentHover: 'pomegranate120',
      accentDisable: 'pomegranate40',
      black: 'blackberry',
      error: 'pomegranate',
      error20: 'pomegranate20',
      focus: 'electricAvocado',
      focus20: 'electricAvocado20',
      hover: 'gray200',
      info: '',
      link: 'blackberry',
      primary: 'blackberry',
      primaryHover: 'blackberry120',
      primaryDisable: 'gray500',
      secondary: 'gray300',
      success: 'electricAvocado',
      success20: 'electricAvocado20',
      text: 'gray700',
      warning: 'mango',
      warning20: 'mango20',
      white: '#fff',
    },

    edgeSize,

    elevation: {
      '1x': { color: 'rgba(0, 0, 0, 0.15)', shadow: '1px 2px 7px 1px' },
      '2x': { color: 'rgba(0, 0, 0, 0.24)', shadow: '0px 10px 20px 0px' },
      '3x': { color: 'rgba(0, 0, 0, 0.1)', shadow: '0px 2px 5px' },
    },

    font: {
      family: {
        sailec: 'sailec',
      },

      size: {
        heroLarge: '2.8rem',
        heroMedium: '2.2rem',
        heroSmall: '1.8rem',
        body: '1.4rem',
        bodyLarge: '1.6rem',
        bodyMedium: '1.2rem',
        bodySmall: '1.0rem',
      },
    },

    outline: {
      color: 'electricAvocado',
      size: 'double',
      style: 'solid',
    },

    zIndex: zIndices,
  },

  banner: {
    info: {
      backgroundColor: 'gray100',
      border: {
        color: 'gray100',
      },
      padding: { vertical: '1.5x', horizontal: '2x' },
      inlineLink: {
        color: 'primary',
        hoverColor: 'primary',
      },
      closeIcon: {
        color: 'primary'
      },
    },
    warning: {
      backgroundColor: 'mango',
      inlineLink: {
        color: 'primary',
        hoverColor: 'primary',
      },
      closeIcon: {
        color: 'primary'
      },
    },
    success: {
      backgroundColor: 'electricAvocado',
      inlineLink: {
        color: 'primary',
        hoverColor: 'primary',
      },
      closeIcon: {
        color: 'primary'
      },
    },
    error: {
      backgroundColor: 'error',
      inlineLink: {
        color: 'white',
        hoverColor: 'white',
      },
      closeIcon: {
        color: 'white'
      },
      font: { color: 'white' },
    },
  },

  barProgress: {
    borderRadius: '100px',
    track: {
      height: '8px',
      color: {
        base: 'secondary',
        progress: 'primary'
      }
    }
  },

  button: {
    base: {
      background: 'accent',
      border: 'none',
      borderRadius: 'none',
      shadow: '2x',
      font: {
        color: 'gray50',
        size: 'body',
        letterSpacing: 'double',
        lineHeight: '1.45',
        textTransform: 'uppercase',
        weight: '700',
      },
      outline: 'none',
      padding: { top: '2.3rem', bottom: '1.9rem', horizontal: '1x' },
      width: { min: '11.25em' },
      loadingIconInsideButton: false,
      spinner: { size: '4x' },
      additionalStyles: '',
    },

    overrides: {
      primary: {
        state: {
          disabled: { background: 'accentDisable' },
          hover: { background: 'accentHover' },
          focus: buttonFocusOverride,
        },
      },

      secondary: {
        base: {
          background: 'transparent',
          shadow: 'none',
          font: { color: 'primary' },
          border: { color: 'primary', size: '2px' },
          padding: { top: '2.25x', bottom: '1.75x', horizontal: '1.7rem' },
        },
        state: {
          disabled: {
            border: { color: 'gray400' },
            font: { color: 'gray400' },
          },
          hover: {
            background: 'gray400',
          },
          focus: buttonFocusOverride,
        },
      },

      link: {
        base: {
          background: 'none',
          font: {
            color: 'link',
            textDecoration: 'underline',
            size: 'body',
            lineHeight: '1.75',
            textTransform: 'none',
            weight: '400',
            letterSpacing: '0',
          },
          border: { size: 'none' },
          padding: 'none',
          shadow: 'none',
          width: { base: 'auto', min: 'auto'},
        },
        state: {
          hover: {
            font: { color: 'text' },
          },
          disabled: { font: { color: 'gray500' } },

          focus: {...buttonFocusOverride, border: 'none', outline: { color: 'electricAvocado' } },
        },
      },

      text: {
        base: {
          background: 'none',
          padding: 'none',
          font: {
            color: 'black',
            weight: '400',
            letterSpacing: '0',
            textTransform: 'none',
            lineHeight: '1.75',
          },
          border: 'none',
          shadow: 'none',
          width: { min: 'auto' },
        },

        state: {
          disabled: {
            font: { color: 'gray500' },
          },
          hover: {
            font: { color: 'text' }
          },
          focus: {...buttonFocusOverride, border: 'none', outline: { color: 'electricAvocado' } },
        },
      },

      action: {
        base: {
          background: 'transparent',
          shadow: 'none',
          font: { color: 'primary', textTransform: 'none', lineHeight: '1.45', weight: 'normal', letterSpacing: '0' },
          borderRadius: '25px',
          border: { color: 'gray400', size: '2px' },
          width: { min: 'auto', base: 'auto' },
          padding: { top: '1.1rem', bottom: '0.9rem', horizontal: '2x' }
        },
        state: {
          active: {
            background: 'primary',
            border: { color: 'primary' },
            font: { color: 'white' }
          },
          disabled: {
            border: { color: 'gray300' },
            font: { color: 'gray500' },
          },
          hover: {
            border: { color: 'primary'}
          },
          focus: {
            border: { color: 'gray400' },
            outline: 'none',
            shadow:  [
              { color: 'white', inset: false, shadow: '0 0 0 2px' },
              { color: 'focus', inset: false, shadow: '0 0 0 4px' },
            ],
          },
          selected: {
            background: 'primary',
            border: { color: 'primary' },
            font: { color: 'white', textTransform: 'none' },
          }
        },
      },

      select: {
        base: {
          background: 'transparent',
          justifyContent: 'space-between',
          shadow: 'none',
          icons: {
            after: {
              icon: 'arrow-bottom',
              width: '3x',
              height: '3x',
              color: 'black',
            }
          },
          font: {
            color: 'black',
            textDecoration: 'none',
            size: 'bodyLarge',
            lineHeight: '',
            textTransform: 'capitalize',
            letterSpacing: 'none',
            weight: '400',
          },
          border: { size: 'double' , color: 'black', side: 'bottom' },
          padding: { horizontal: '0', top: '2x', bottom: '0.57em' },
        },
        state: {
          hover: {},
          focus: {...buttonFocusOverride, outline: { color: 'electricAvocado' } },
          error: {
            border: { color: 'error' },
            icons: {
              after: {
                icon: 'arrow-bottom',
                width: '3x',
                height: '3x',
                color: 'black',
              },
            },
          },
          disabled: {
            border: { color: 'gray400' },
            font: { color: 'gray500' },
            icons: {
              after: {
                icon: 'arrow-bottom',
                width: '3x',
                height: '3x',
                color: 'gray500',
              },
            },
          },
        },
      }
    },
  },

  containerSize: {
    full: '100%',
    narrow: '', // 512px
    'narrow-2': '', // 704px
    wide: '', // 896px
    max: '', // 1280px
  },

  circleProgress: {
    strokeWidth: 2.75,
    backgroundPathColor: 'gray300',
    percentagePathColor: 'pomegranate',
    label: {
      percentage: {
        sign: {
          size: 'body',
          weight: '700',
          margin: { left: 'double' }
        },
        number: {
          size: 'heroLarge',
          lineHeight: '0.74',
          color: 'black',
          margin: { bottom: 'quadruple' },
          verticalAlign: 'super',
        },
      },
      content: {
        size: 'body',
        lineHeight: '1.45',
        color: 'gray600',
      },
    }
  },

  datePicker: {
    input: {
      // tslint:disable:max-line-length
      icon: (color: string) => `"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M12.5007 13.6321L5.683 7.26892L4.31836 8.73103L12.5007 16.3679L20.683 8.73103L19.3184 7.26892L12.5007 13.6321Z' fill='${encodeURIComponent(color)}'/%3E%3C/svg%3E%0A"`,
      iconColor: {
        base: 'text',
        disabled: 'gray500'
      },
      valueFormat: 'MM/DD/YYYY'
    },
    navButton: {
      color: 'pomegranate',
      nextIcon: (color: string) => `"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' %3E%3Cpath fill='${encodeURIComponent(color)}' d='M8 0L6.59 1.41 12.17 7H0v2h12.17l-5.58 5.59L8 16l8-8z' fillRule='nonzero' /%3E%3C/svg%3E"`,
      prevIcon: (color: string) => `"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' %3E%3Cpath fill='${encodeURIComponent(color)}' d='M8 0l1.41 1.41L3.83 7H16v2H3.83l5.58 5.59L8 16 0 8z' fillRule='nonzero' /%3E%3C/svg%3E"`,
      // tslint:enable:max-line-length
      backgroundSize: '100%'
    },
    monthLabel: {
      fontSize: 'heroSmall'
    },
    weekdayLabel: {
      color: 'inherit',
      fontSize: 'bodySmall',
      fontWeight: '700',
      padding: { top: '5px' }
    },
    dayCell: {
      borderRadius: { topLeft: '2px', bottomLeft: '2px', topRight: '2px', bottomRight: '2px' },
      selected: {
        color: 'blackberry',
        backgroundColor: 'gray400',
      },
      disabled: {
        color: 'gray500'
      },
      hover: {
        color: 'blackberry',
        backgroundColor: 'gray400',
      },
      fontSize: 'bodyLarge',
      leadingZeros: true,
      padding: { top: '1x', left: '1x', right: '1x', bottom: 'quadruple' }
    },
    footer: {
      margin: { top: '1x' }
    },
    overlay: {
      borderRadius: '0',
      border: { size: '.25px', color: 'gray300' },
      boxShadow: '1x',
      padding: '4x',
    },
    dayPickerProps: {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      weekdaysShort: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
    },
    focusRing: {
      outline: {
        color: 'electricAvocado',
        size: 'double',
        style: 'solid',
      },
    },
  },

  descriptionList: {
    term: {
      family: 'sailec',
      weight: '700',
      color: 'black',
      size: 'body',
      margin: { right: '3x' },
      lineHeight: '1.72',
    },

    detail: {
      family: 'sailec',
      weight: '400',
      color: 'black',
      size: 'body',
      lineHeight: '1.72',
    },

    variant: {
      large: {
        term: {
          weight: '700',
          lineHeight: '1.5',
          size: 'bodyMedium',
        },
        detail: {
          size: 'heroLarge',
          lineHeight: '1.3',
        }
      },

      small: {
        term: {
          weight: '700',
          lineHeight: '1.5',
          size: 'bodyMedium',
        },
        detail: {
          weight: '400',
          size: 'heroMedium',
          lineHeight: '1.47'
        }
      },
    }
  },

  form: {
    autoComplete: {
      list: {
        backgroundColor: 'white',
        shadow: '1x',
        height: { max: '36.4rem' },
      },

      listItem: {
        margin: { top: '1x', bottom: '1x' },
        padding: { top: '1x', bottom: '0.75x', horizontal: '2x'},
        font: {
          color: 'primary',
          size: 'body',
          lineHeight: '1.43'
        },
        active: {
          backgroundColor: 'primary',
          font: {
            color: 'white',
          },
        },
      },

      listItemHeader: {
        font: {
          color: 'primary',
          size: 'bodyMedium',
          lineHeight: '1.5',
          weight: '700',
        },
      },
    },

    checkbox: {
      backgroundColor: 'white',
      border: { size: 'double', color: 'gray700' },
      icon: {
        size: '2.25x',
        color: 'primary'
      },
      label: {
        font: { color: 'gray700', size: 'bodyLarge', lineHeight: '1.5' },
      },
      round: 'none',
      size: '2.25x',
      margin: { right: '1x' },

      state: {
        checked: {
          backgroundColor: 'electricAvocado',
          border: { size: 'double', color: 'primary' },
        },

        disabled: {
          border: { size: 'double', color: 'gray500' },
          label: { font: { color: 'gray500' }},
        },

        disabledChecked: {
          backgroundColor: 'gray200',
          icon: {
            color: 'gray500',
            size: '2.25x',
          },
        },

        error: {
          border: { size: 'double', color: 'error' },
          label: { font: { color: 'error' }},
        },

        focus: {
          focusPosition: 'labelAndIndicator',
          outline: { offset: '0' },
        },

        hover: {
          border: { size: 'double', color: 'primary' },
          label: { font: { color: 'primary' }},
        },
      },
    },

    layout: {
      wrapper: {
        row: { margin: { bottom: '3x' } },
        stackRow: {
          margin: {
            base: { right: '0' },
            small: { right: '3x' },
          },
        },
      },
    },

    errorText: {
      font: {
        color: 'error',
        size: 'body',
        lineHeight: '1.45'
      },
      margin: { top: 'quadruple' },
    },

    helperText: {
      font: {
        color: { default: 'text', error: 'error', hover: 'primary' },
        size: 'body',
        weight: '400',
        lineHeight: '1.45'
      },
      margin: { top: 'quadruple' },
    },

    input: {
      backgroundColor: 'transparent',
      border: { side: 'bottom', color: 'gray600', size: 'double' },
      iconColor: 'gray600',
      padding: { horizontal: 'none', top: '1.2rem', bottom: '1rem' },
      placeholder: 'gray700',
      round: 'none',
      font: { color: 'black', lineHeight: '1.5' },

      state: {
        hover: {
          border: { side: 'bottom', color: 'primary', size: 'double' },
        },

        focus: {
          outline: { color: 'focus', size: 'double', offset: 'double' }
        },

        error: {
          border: { side: 'bottom', color: 'error', size: 'double' },
        },

        disabled: {
          border: { side: 'bottom', color: 'gray400', size: 'double' },
          backgroundColor: 'transparent',
          font: { color: 'gray500' }
        },
      },
    },

    label: {
      font: {
        color: {
          default: 'text',
          disabled: 'primaryDisable',
          error: 'error',
          hover: 'primary',
        },
        size: 'body',
        weight: '400',
      },
      margin: { bottom: 'quadruple' },
    },

    legend: {
      font: {
        size: 'body',
        color: 'text',
      },
      margin: { bottom: '1x' },
    },

    radio: {
      badge: {
        backgroundColor: 'transparent',
        border: { color: 'gray700', size: 'double' },
        padding: { top: '1rem', bottom: '.8rem', left: '2.5x', right: '2.5x' },
        round: 'none',
        width: { min: '4.8125em' },
        font: { color: 'text', lineHeight: '1.3', size: 'body' },

        state: {
          checked: {
            border: { color: 'primary', size: 'double' },
            backgroundColor: 'primary',
            font: { color: 'white' },
          },

          checkedHover: {
            font: { color: 'white' },
            border: { color: 'primary', size: 'double' },
          },

          focus: {
            outline: { color: 'electricAvocado', size: 'double', style: 'solid', offset: 'triple' }
          },

          hover: {
            border: { color: 'blackberry', size: 'double' },
            font: { color: 'blackberry' },
          },

          error: {
            border: { color: 'error', size: 'double' },
          },

          disabled: {
            backgroundColor: 'gray300',
            border: { color: 'gray500', size: 'double' },
            font: { color: 'gray500' },
          },

          disabledChecked: {
            backgroundColor: 'gray400',
            border: { color: 'gray400', size: 'double' },
            font: { color: 'white' },
          },
        },
      },

      button: {
        backgroundColor: 'white',
        checkedStyle: 'solid',
        checkedBgColor: 'primary',
        border: { size: 'double', color: 'gray700'},
        label: {
          font: { lineHeight: '1.25' },
          margin: { left: '3.5x' },
        },
        size: '2.5x',

        state: {
          checked: {
            shadow: { color: 'white', inset: true, shadow: '0 0 0 4px' },
            border: { color: 'primary', size: 'double' },
          },

          disabled: {
            backgroundColor: 'gray200',
            border: { color: 'primaryDisable', size: 'double' },
            label: { font: { color: 'primaryDisable' }},
          },

          disabledChecked: {
            backgroundColor: 'primaryDisable',
          },

          error: {
            border: { size: 'double', color: 'error' },
            label: { font: { color: 'error' } },
          },

          focus: {
            focusPosition: 'labelAndIndicator',
          },

          hover: {
            border: { color: 'primary', size: 'double' },
          },
        },
      },

      card: {
        backgroundColor: 'white',
        border: { color: 'gray100', size: '0.25px' },
        round: 'none',
        shadow: '1x',

        state: {
          checked: {
            backgroundColor: 'electricAvocado20',
            border: { color: 'electricAvocado', size: 'double' },
          },

          hover: {
            border: { color: 'electricAvocado', size: 'double' },
          },

          focus: {
            border: { color: 'transparent', size: 'double' },
            outline: { offset: '5px', style: 'auto' },
          },

          error: {
            text: {
              margin: { top: '3x' }
            }
          },
        },
      },

      toggle: {
        label: { color: 'gray700', size: 'body', lineHeight: '1.45' },

        button: {
          padding: { vertical: '0.75x', horizontal: '0.75x' },
          border: { color: 'gray700', size: 'double' },
          backgroundColor: 'white',
          additionalStyles: css`transform: translateY(-1.5px);`,
        },

        slidingIndicator: { size: '1x', color: 'gray700' },

        state: {
          checked: {
            label: { color: 'black' },
            button: {
              border: { color: 'black', size: 'double' },
              backgroundColor: 'black',
            },
            slidingIndicator: { color: 'success' },
          },

          disabled: {
            label: { color: 'gray500' },
            button: {
              border: { color: 'gray500', size: 'double' },
              backgroundColor: 'white',
            },
            slidingIndicator: { color: 'gray500' },
          },

          disabledChecked: {
            label: { color: 'gray500' },
            button: {
              border: { color: 'gray500', size: 'double' },
              backgroundColor: 'gray500',
            },
            slidingIndicator: { color: 'gray100' },
          },

          focus: {
            label: { outline: { offset: 'single' } },
          },

          hover: {
            label: { color: 'black' },
            button: {
              border: { color: 'black', size: 'double' },
              backgroundColor: 'white',
            },
            slidingIndicator: { color: 'black' },
          },
        },
      },
    },

    select: {
      icon: {
        size: '3x',
        type: 'select-input-arrow',
        color: 'gray700',
      },
    },

    slider: {
      outline: { color: 'focus' },
      thumb: {
        color: { default: 'blackberry120', disabled: 'gray500' },
        height: 36,
        width: 36,
      },
      track: {
        color: {
          base: { default: 'gray200', disabled: 'gray100' },
          progress: { default: 'blackberry', disabled: 'gray300' }
        },
        thickness: 8,
        borderRadius: '4px',
      },
      unitLabels: {
        color: { default: 'black', disabled: 'gray700' },
        margin: { top: '2x' },
      }
    },

    textarea: {
      backgroundColor: 'transparent',
      border: { color: 'gray600' },
      font: { color: 'gray700' },
      padding: { horizontal: '2x', top: '1.5x', bottom: '1.5x' },

      state: {
        hover: {
          border: { color: 'black', },
          font: { color: 'black' },
        },

        focus: {
          font: { color: 'black' },
          outline: { color: 'focus', offset: 'double' }
        },

        error: {
          border: { color: 'error' },
        },

        disabled: {
          border: { color: 'gray400' },
          font: { color: 'gray500' },
        },
      }
    }
  },

  heading: {
    default: {
      color: 'primary',
      family: 'sailec',
      lineHeight: '1.43',
    },

    level: {
      h1: {
        size: 'heroLarge',
      },

      h2: {
        size: 'heroMedium',
        lineHeight: '1.47',
      },

      h3: {
        size: 'heroSmall',
        lineHeight: '1.6',
      },

      h4: {/* level specific styles */},
      h5: {/* level specific styles */},
      h6: {/* level specific styles */},
    },
  },

  layout: {
    header: {
      shadow: '3x',
      padding: {
        base: { horizontal: '3x' },
        large: { horizontal: '10x' },
        xlarge: { horizontal: '5%' },
      },
      height: { base: '6.4rem', small: '8rem' },
      margin: { top: 'quadruple'},

      dropdown: {
        shadow: '1x',
        width: { base: '100%', small: '24rem' },
        backgroundColor: 'white',
        border: 'none',
        borderRadius: { base: 'none', small: '1x'},
        padding: { vertical: '1x' },
        icon: {
          desktop: {
            size: '2.75x',
            color: 'black',
            type: 'arrow-bottom',
          },
        },

        button: {
          font: {
            color: 'black',
            size: 'body',
            lineHeight: '1.72',
          },

          state: {
            hover: { font: { color: 'accent' }},
          }
        },

        link: {
          divider: {
            placement: VALID_PLACEMENTS.lastChildOnly,
            height: { base: '0', small: '1px' },
            margin: { small: { vertical: '1x' }},
            backgroundColor: 'gray400',
            width: { small: 'calc(100% - 3.2rem)'}
          },
          border: { size: 'double', color: 'transparent' },
          padding: { vertical: '0.75x', horizontal: '2x' },
          font: {
            color: 'gray600',
            lineHeight: '1.72',
            size: 'body',
            weight: '700',
            align: { base: 'center', small: 'left' },
          },
          state: {
            hover: { font: { color: 'accent' }},
            focus: { border: { size: 'double', color: 'focus' }},
          },
        }
      },

      link: {
        font: {
          color: 'black',
          lineHeight: '1.72',
          size: 'body',
          weight: '700',
        },
        margin: { right: '6x' },
        state: {
          hover: { font: { color: 'accent' }},
        },
      },
    },
  },

  list: {
    margin: { left: '2x' },

    listItem: {
      common: {
        margin: { bottom: '1x' },
        font: {
          family: 'sailec',
          lineHeight: '1.75',
          size: 'body',
          color: 'black',
          weight: '400'
        },
      },

      bullet: {
        backgroundColor: 'black',
        listStyleSpacing: '2.6rem',
        size: '0.75x',
      },

      icon: {
        listStyleSpacing: '1.5x',
        size: '2.5x',
        type: 'done',
        color: 'black',
      },

      number: {
        listStyleSpacing: '3.4rem',
        font: {
          weight: '400',
          color: 'black',
        },
      },
    },
  },

  link: {
    inline: {
      state: {
        hover: { color: 'gray700' },
      },
    },
  },

  modal: {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      padding: {
        base: { top: '3x' },
        medium: { top: '11x' },
      },
    },
    card: {
      backgroundColor: 'white',
      border: { color: 'gray100', size: '.25px' },
      padding: {
        base: { top: '6x', bottom: '3x', horizontal: '3x' },
        medium: '6x',
      },
      shadow: '1x'
    },
    closeIcon: {
      type: 'alert-close',
      size: '2x',
      color: 'black',
      padding: '2.5x'
    },
  },

  notification: {
    variant: {
      success: {
        border: {
          side: 'left',
          size: '6px',
          color: 'electricAvocado',
          style: 'solid'
        },
        shadow: { shadow: '-2px 8px 8px 4px', color: 'rgba(0, 0, 0, 0.1)' },
        padding: { vertical: '2x', horizontal: '3x'}
      },
      error: {
        border: {
          side: 'left',
          size: '6px',
          color: 'error',
          style: 'solid'
        },
        shadow: { shadow: '-2px 8px 8px 4px', color: 'rgba(0, 0, 0, 0.1)' },
        padding: { vertical: '2x', horizontal: '3x'}

      },
      warning: {
        border: {
          side: 'left',
          size: '6px',
          color: 'warning',
          style: 'solid'
        },
        shadow: { shadow: '-2px 8px 8px 4px', color: 'rgba(0, 0, 0, 0.1)' },
        padding: { vertical: '2x', left: '3x', right: '6x'}

      },
    },
    closeIcon: {
      size: '1.5x',
      color: 'primary',
      padding: { left: '1x' }
    }
  },

  spinner: {
    size: '7em',
    withOutline: true,
    strokeLinecap: 'round',
    color: {
      circleOutline: 'gray400',
      animatedOutline: 'blackberry',
    },
  },

  table: {
    border: 'none',
    padding: {
      base: { vertical: '2x' },
    },

    tableHeader: {
      backgroundColor: 'gray100',
      rowDivider: 'gray400',
      padding: {
        base: { vertical: '2x', horizontal: '3x' },
        small: { top: '2.5x', horizontal: '3x', bottom: '1x' },
      },
      font: {
        size: 'bodyMedium',
        lineHeight: '1.125',
        textTransform: 'uppercase',
      },
    },

    tableBody: {
      rowDivider: 'gray400',
      padding: {
        base: { vertical: '2x', horizontal: '3x' },
        small: { vertical: '2.5x', horizontal: '3x' },
      },
      font: {
        size: 'body',
        lineHeight: '1.25',
      },
    },
  },

  tooltip: {
    activationType: 'automatic',
    pane: {
      background: 'white',
      padding: '2x',
      shadow: '1x',
      round: 'double',
      verticalOffset: '8%',
      position: 'top',

      font: {
        size: 'body',
        color: 'primary',
      },
    },

    icon: {
      size: '1.125em',
      color: 'primary',
      hover: 'primaryHover',
    },
  },

  typography: {
    text: {
      body: {
        default: {
          color: 'black',
          lineHeight: '1.5',
          family: 'sailec',
        },
        large: { lineHeight: '1.45' },
        small: { lineHeight: '1.8' },
      },
    },

    pill: {
      default: {
        padding: { top: '0.75x', bottom: 'double', horizontal: '1x' },
        font: {
          transform: 'uppercase',
          color: 'white',
          family: 'sailec',
          size: 'bodySmall',
          lineHeight: '1.6',
          weight: '700',
        },
        round: 'double',
      },
      neutral: { backgroundColor: 'gray700' },
      success: {
        backgroundColor: 'success',
        font: { color: 'black' }
      },
      warning: {
        backgroundColor: 'warning',
        font: { color: 'black' }
      },
      error: { backgroundColor: 'error' },
      info: { backgroundColor: 'black' },
    },
  },
});

export default theme;
